import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/** The Stoke type roles (src/css/ui/stoke-type.css), as `text-stoke-*`. */
const STOKE_TEXT_SIZES = [
  "stoke-micro",
  "stoke-meta",
  "stoke-body",
  "stoke-read",
  "stoke-lede",
  "stoke-title",
];

/**
 * The Goldfinch type scale (src/css/goldfinch/typography.css), as `text-gf-*`.
 * `gf-eyebrow` stays out: GfEyebrow pairs it with a tone colour that retires
 * it, and the marketing eyebrows are sized around that.
 */
const GOLDFINCH_TEXT_SIZES = [
  "gf-caption",
  "gf-body",
  "gf-body-lg",
  "gf-lead",
  "gf-title-sm",
  "gf-title",
  "gf-headline",
  "gf-hero",
];

/** The Owl type scale (src/ui/design/owl/owl.css), as `text-owl-*`. */
const OWL_TEXT_SIZES = [
  "owl-micro",
  "owl-label",
  "owl-ui",
  "owl-body",
  "owl-title",
  "owl-display",
  "owl-reel",
  "owl-poster",
];

/** The Goldfinch radii (src/css/goldfinch/layout.css), as `rounded-gf-*`. */
const GOLDFINCH_RADII = [
  "gf-xs",
  "gf-sm",
  "gf-md",
  "gf-lg",
  "gf-xl",
  "gf-pill",
];

/**
 * `owl-ring` and `owl-ring-inset` (src/ui/design/owl/owl.css) are outlines.
 * Naming them here lets a later ring retire an earlier `outline-*` under the
 * same variant, the way `ring-0` retires `ring-3`, so a substrate's own
 * focus outline never outranks the Owl ring.
 *
 * The two sheens are one `background-image` each, so a later one retires
 * an earlier one the way `bg-*` colours do.
 *
 * A `text-stoke-*`, `text-gf-*` or `text-owl-*` size is a font size. Unregistered, the
 * merger reads it as a text colour, and a later `text-foreground` retires it.
 *
 * A `rounded-gf-*` radius is unknown to the merger too, so a component's own
 * `rounded-lg` would survive beside it and win on stylesheet order.
 */
const twMerge = extendTailwindMerge<"owl-ring" | "owl-sheen">({
  extend: {
    theme: { radius: GOLDFINCH_RADII },
    classGroups: {
      "font-size": [
        {
          text: [
            ...STOKE_TEXT_SIZES,
            ...GOLDFINCH_TEXT_SIZES,
            ...OWL_TEXT_SIZES,
          ],
        },
      ],
      "owl-ring": ["owl-ring", "owl-ring-inset"],
      "owl-sheen": ["owl-lit", "owl-sunk"],
    },
    conflictingClassGroups: {
      "owl-ring": [
        "outline-w",
        "outline-style",
        "outline-color",
        "outline-offset",
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
